import { useRef, useState } from 'react';

import { type FetchError, fetchResult, type RequestOptions } from 'utils/fetch';

import { useAsyncEffect } from './useAsyncEffect';

interface Options extends RequestOptions {
	/** If the fetch should run. */
	isActive?: boolean;
}

/**
 * For simple one time fetches where SWR is too much. No caching, retries or
 * revalidation.
 */
export function useFetchOnce<T>(
	input: string | URL | Request,
	{ isActive = true, ...options }: Options = {},
) {
	const [data, setData] = useState<T | undefined>();
	const [error, setError] = useState<FetchError | undefined>();
	const [isLoading, setIsLoading] = useState(isActive);
	const hasFetchedRef = useRef(false);

	useAsyncEffect(
		async ({ isMounted }) => {
			if (hasFetchedRef.current || !isActive) {
				return;
			}
			hasFetchedRef.current = true;
			setIsLoading(true);
			const result = await fetchResult<T>(input, options);
			if (!isMounted()) {
				return;
			}
			setIsLoading(false);
			if (result.isSuccess()) {
				setData(result.value);
			} else {
				setError(result.error);
			}
		},
		[isActive],
	);

	return { data, error, isLoading };
}
